// Coverage Radar — what protection a household holds vs. what its life stage needs.
// Needs follow the legacy Needs Center logic (v62/v79: life, children, retirement, health, tax)
// made explicit: every need carries the reason it applies. Held cover is classified from the
// policy: its explicit category first, otherwise branch + product vocabulary in Greek and English.
// Nothing is invented: unknown products fall back to their branch. WHO is covered (an insured person
// of a policy in force today, member-specific benefits) comes from the domain's coverageForPerson.
import {
  ageOn, benefitCategory, childrenOfPerson, coverageForPerson, heldInForce, minorChildren, searchKey, normalizeNeedLines,
  type Day, type NeedArea, type NeedsAnalysis, type PersonCover, type Policy, type PolicyCategory, type WorkspaceView,
} from '@vero/domain';

export const COVERAGE_AREAS = ['life', 'health', 'income', 'savings', 'education', 'property'] as const;
export type CoverageArea = typeof COVERAGE_AREAS[number];

const VOCAB: [CoverageArea, RegExp][] = [
  ['education', /(educat|σπουδ|εκπαιδ|school|σχολ|student|φοιτητ)/],
  ['health', /(medic|hospital|health|υγει|νοσοκομ|ιατρ|clinic|κλινικ|care)/],
  ['income', /(disab|income|critical|accident|ατυχ|ανικαν|σοβαρ|εισοδημ|waiver|salary|μισθ)/],
  ['savings', /(saving|invest|pension|retire|capital|fund|αποταμ|συνταξ|επενδ|builder|horizon|unit link|endow|προικ)/],
  ['property', /(home|house|motor|(^|\s)cars?(\s|$)|fire|travel|property|κατοικ|οχημ|αυτοκιν|(^|\s)πυρ|ταξιδ|σπιτ|liability|αστικ)/],
  ['life', /(life|ζωη|ζωης|term|protect|θανατ|family|οικογεν|mortgage|στεγαστ)/],
];

/** explicit policy category → radar areas (group / other say nothing: the product name decides) */
const CATEGORY_AREAS: Record<PolicyCategory, CoverageArea[] | null> = {
  life: ['life'], credit_life: ['life'], medical: ['health'], savings: ['savings'], pension: ['savings'], education: ['education'],
  accident: ['income'], critical_illness: ['income'], disability: ['income'],
  motor: ['property'], home: ['property'], travel: ['property'], liability: ['property'], business: ['property'], group: null, other: null,
};
export function classifyPolicy(p: Pick<Policy, 'productName' | 'branch'> & { category?: PolicyCategory | null }): CoverageArea[] {
  // the advisor's category wins over the product name (a «medical» plan called «Family Plan» is health, not life)
  const byCategory = p.category ? CATEGORY_AREAS[p.category] : null;
  if (byCategory) return [...byCategory];
  const k = searchKey(p.productName);
  const hits = VOCAB.filter(([, re]) => re.test(k)).map(([a]) => a);
  if (hits.length) {
    // combined life plans with savings count for both
    if (hits.includes('savings') && p.branch === 'life' && !hits.includes('life')) hits.push('life');
    return [...new Set(hits)];
  }
  return p.branch === 'general' ? ['property'] : ['life'];
}

/** the areas one policy covers for one person: the policy's own areas plus that person's benefits (a rider recorded for one child adds nothing to the others) */
export function coverAreas(c: Pick<PersonCover, 'policy' | 'benefits'>): CoverageArea[] {
  const out = new Set(classifyPolicy(c.policy));
  for (const b of c.benefits) { const cat = benefitCategory(b); for (const a of (cat && CATEGORY_AREAS[cat]) || []) out.add(a); }
  return COVERAGE_AREAS.filter((a) => out.has(a));
}

export type NeedLevel = 0 | 1 | 2 | 3; // none, nice, important, essential
/** Needs Analysis areas (11, spec §8) folded into the six radar areas. */
export const NEED_TO_COVERAGE: Record<NeedArea, CoverageArea> = {
  life: 'life', mortgage: 'life', income: 'income', critical_illness: 'income', accident: 'income', health: 'health',
  retirement: 'savings', savings: 'savings', education: 'education', property: 'property', business: 'property',
};
export function latestFinalNeeds(v: WorkspaceView, personId: string): NeedsAnalysis | undefined {
  return v.where('needs_analyses', 'personId', personId).filter((n) => n.status === 'final').sort((a, b) => (b.finalizedAt ?? b.date).localeCompare(a.finalizedAt ?? a.date))[0];
}
export interface AreaStatus {
  area: CoverageArea;
  need: NeedLevel;
  needReason: string;            // i18n key
  /** advisor's own reasoning when the need comes from a Needs Analysis */
  note?: string | null;
  held: 0 | 0.5 | 1;             // none / offered-pending / active
  policies: string[];            // policy ids
  gap: boolean;                  // important/essential need without active cover
}
export interface CoverageProfile { areas: AreaStatus[]; score: number; gaps: CoverageArea[]; minors: number; age: number | null; source: 'life_stage' | 'needs_analysis'; needsId?: string }


export function coverageProfile(v: WorkspaceView, personId: string, today: Day): CoverageProfile {
  const p = v.get('persons', personId)!;
  const age = p.birthDate ? ageOn(p.birthDate, today) : null;
  // children from the household (dependants included) and from the Family tab, once per person
  const kids = minorChildren(childrenOfPerson(v, personId, today, { dependents: true }));
  const minors = kids.length;
  const married = /married|παντρ|εγγαμ|σύζυγ|συζυγ/i.test(p.maritalStatus ?? '');
  const retired = /retir|συνταξιουχ/i.test(searchKey(p.occupation ?? '')) || (age !== null && age >= 67);
  const working = !retired && (age === null || (age >= 18 && age < 67));
  const need: Record<CoverageArea, [NeedLevel, string]> = {
    life: minors ? [3, 'coverage.reason.dependents'] : married ? [2, 'coverage.reason.partner'] : working ? [1, 'coverage.reason.debts'] : [0, 'coverage.reason.none'],
    health: [3, 'coverage.reason.health'],
    income: working ? [minors || married ? 3 : 2, 'coverage.reason.income'] : [0, 'coverage.reason.none'],
    savings: age !== null && age >= 60 ? [1, 'coverage.reason.retiring'] : working ? [2, 'coverage.reason.retirement'] : [1, 'coverage.reason.retirement'],
    education: minors ? [2, 'coverage.reason.children_education'] : [0, 'coverage.reason.none'],
    property: [1, 'coverage.reason.property'],
  };
  // a finalised Needs Analysis is the advisor's judgement and overrides the life-stage defaults
  const na = latestFinalNeeds(v, personId);
  const notes: Partial<Record<CoverageArea, string>> = {};
  if (na) {
    const lines = normalizeNeedLines(na.areas);
    for (const a of COVERAGE_AREAS) {
      const mine = lines.filter((l) => NEED_TO_COVERAGE[l.area] === a);
      const top = mine.reduce((m, l) => (l.priority > m.priority ? l : m), mine[0]!);
      need[a] = [top.priority, 'coverage.reason.needs_analysis'];
      const txt = mine.filter((l) => l.priority > 0 && l.reasoning).map((l) => l.reasoning).join(' · ');
      if (txt) notes[a] = txt;
    }
  }
  // the person's own cover: policies on which they are an insured person, in force today (offers / pending: 0.5)
  const held: Record<CoverageArea, { level: 0 | 0.5 | 1; ids: string[] }> = Object.fromEntries(COVERAGE_AREAS.map((a) => [a, { level: 0, ids: [] }])) as never;
  const mark = (a: CoverageArea, lvl: 0.5 | 1, id: string) => { if (lvl > held[a].level) held[a].level = lvl; if (!held[a].ids.includes(id)) held[a].ids.push(id); };
  for (const c of coverageForPerson(v, personId, today, { pending: true })) for (const a of coverAreas(c)) mark(a, c.state === 'in_force' ? 1 : 0.5, c.policy.id);
  // the children's education is a family need: a plan the person holds for a child, or one covering a child, meets it
  if (kids.length) {
    for (const pol of heldInForce(v, personId, today)) if (classifyPolicy(pol).includes('education')) mark('education', 1, pol.id);
    for (const k of kids) if (k.personId) for (const c of coverageForPerson(v, k.personId, today)) if (coverAreas(c).includes('education')) mark('education', 1, c.policy.id);
  }
  const areas: AreaStatus[] = COVERAGE_AREAS.map((a) => ({ area: a, need: need[a][0], needReason: need[a][1], note: notes[a] ?? null, held: held[a].level, policies: held[a].ids, gap: need[a][0] >= 2 && held[a].level < 1 }));
  const weight = areas.reduce((s, x) => s + x.need, 0) || 1;
  const score = Math.round((areas.reduce((s, x) => s + x.need * x.held, 0) / weight) * 100);
  return { areas, score, gaps: areas.filter((x) => x.gap).map((x) => x.area), minors, age, source: na ? 'needs_analysis' : 'life_stage', needsId: na?.id };
}
